import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Zoom,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Add,
  ZoomIn,
  ZoomOut,
  Create,
  DateRange,
  Delete,
  Download
} from '@mui/icons-material';
import DocumentSignatureField, { SignatureField } from './DocumentSignatureField';

interface DocumentViewerProps {
  documentId: string;
  documentUrl?: string;
  onSaveFields?: (fields: SignatureField[]) => void;
  onDownload?: () => void;
  mode?: 'edit' | 'view';
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  documentUrl,
  onSaveFields,
  onDownload,
  mode = 'edit'
}) => {
  const [fields, setFields] = useState<SignatureField[]>([]);
  const [zoom, setZoom] = useState(1);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [isAddingField, setIsAddingField] = useState<'signature' | 'date' | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isEditing = mode === 'edit';

  // Load saved fields
  useEffect(() => {
    // TODO: Fetch saved fields from API
    const savedFields = localStorage.getItem(`doc_fields_${documentId}`);
    if (savedFields) {
      setFields(JSON.parse(savedFields));
    }
  }, [documentId]);

  // Save fields
  useEffect(() => {
    if (onSaveFields) {
      onSaveFields(fields);
    }
    localStorage.setItem(`doc_fields_${documentId}`, JSON.stringify(fields));
  }, [fields, documentId, onSaveFields]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleAddField = (fieldType: 'signature' | 'date') => {
    setIsAddingField(fieldType);
    setShowAddMenu(false);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isAddingField || !isEditing) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left) / zoom - 75;
    const y = (e.clientY - rect.top) / zoom - 25;

    const newField: SignatureField = {
      id: `field_${Date.now()}`,
      type: isAddingField,
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: 150,
      height: 50,
      label: isAddingField === 'signature' ? 'Signature' : 'Date'
    };

    setFields(prev => [...prev, newField]);
    setIsAddingField(null);
  };

  const handleUpdateField = (updatedField: SignatureField) => {
    setFields(prev => prev.map(f => f.id === updatedField.id ? updatedField : f));
  };

  const handleDeleteField = (fieldId: string) => {
    setFields(prev => prev.filter(f => f.id !== fieldId));
  };

  const handleDragStart = () => {
    setActiveFieldId(null);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Toolbar */}
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          mb: 2,
          borderRadius: 1
        }}
      >
        {isEditing && (
          <>
            <Tooltip title="Add Signature Field">
              <IconButton
                size="small"
                onClick={() => setShowAddMenu(!showAddMenu)}
                color="primary"
              >
                <Add />
              </IconButton>
            </Tooltip>

            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={handleZoomIn}>
                <ZoomIn />
              </IconButton>
            </Tooltip>

            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={handleZoomOut}>
                <ZoomOut />
              </IconButton>
            </Tooltip>

            <Box sx={{ flex: 1 }} />

            <Typography variant="caption" color="text.secondary">
              {Math.round(zoom * 100)}%
            </Typography>

            <Tooltip title="Download">
              <IconButton size="small" onClick={onDownload}>
                <Download />
              </IconButton>
            </Tooltip>
          </>
        )}

        {!isEditing && (
          <>
            <Box sx={{ flex: 1 }} />

            <Tooltip title="Download">
              <IconButton size="small" onClick={onDownload}>
                <Download />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Paper>

      {/* Add field menu */}
      <Dialog open={showAddMenu} onClose={() => setShowAddMenu(false)}>
        <DialogTitle>Add Field</DialogTitle>
        <DialogContent>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleAddField('signature')}>
                <ListItemText 
                  primary="Signature Field" 
                  secondary="Placeholder for signatures"
                />
                <Create color="primary" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleAddField('date')}>
                <ListItemText 
                  primary="Date Field" 
                  secondary="Placeholder for dates"
                />
                <DateRange color="success" />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>

      {/* Adding field instruction */}
      {isAddingField && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            p: 2,
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Typography variant="body2">
            Click on the document to place {isAddingField === 'signature' ? 'signature' : 'date'} field
          </Typography>
        </Paper>
      )}

      {/* Field counter */}
      {fields.length > 0 && (
        <Chip
          label={`${fields.length} fields`}
          size="small"
          sx={{ mb: 1 }}
        />
      )}

      {/* Document canvas */}
      <Box
        ref={containerRef}
        onClick={handleCanvasClick}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: '600px',
          bgcolor: 'grey.100',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'auto',
          cursor: isAddingField ? 'crosshair' : 'default',
          p: 4
        }}
      >
        {/* Document container (scaled) */}
        <Box
          sx={{
            position: 'relative',
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            mx: 'auto',
            width: '8.5in',
            minHeight: '11in',
            bgcolor: 'white',
            boxShadow: 3
          }}
        >
          {/* Document content placeholder */}
          {documentUrl ? (
            <iframe
              src={documentUrl}
              style={{
                width: '100%',
                height: '11in',
                border: 'none'
              }}
              title="Document Viewer"
            />
          ) : (
            <Box sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Document Preview
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Document content would appear here. Upload a PDF or generate a document.
              </Typography>
            </Box>
          )}

          {/* Signature fields */}
          {fields.map(field => (
            <DocumentSignatureField
              key={field.id}
              field={field}
              onUpdate={handleUpdateField}
              onDelete={() => handleDeleteField(field.id)}
              onDragStart={handleDragStart}
              isEditing={isEditing}
              scale={zoom}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentViewer;

